import {
  buildPath,
  buildTemplatePath,
  Button,
  CloseButton,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { ContributorBulkMerge, ContributorBulkMergeResource } from "generated/openApi";
import produce from "immer";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { Contributors } from "./Contributors";
import { ContributorEmployments } from "./ContributorEmployments/ContributorEmployments";

const routeName = "contributorBulkMerge";

export const contributorBulkMergePageRoute: PageRoute = {
  handle: "Merge Contributors",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ContributorBulkMergePageState = {
  selectedIds: number[];
};

export const ContributorBulkMergePage = () => {
  const steps: string[] = ["Merge Contributors", "Merge Employments"];

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);

  const { state } = useLocation();
  const [stateParam] = useState<ContributorBulkMergePageState>(state);

  const [bulkMerge, setBulkMerge] = useState<ContributorBulkMerge>({});
  const [resource, setResource] = useState<ContributorBulkMergeResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkMerge] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [activeStep, setActiveStep] = useState<number>(0);

  useEffect(() => {
    getBulkMerge(stateParam.selectedIds);
    fetchResource();
  }, []);

  const getBulkMerge = (contributorIds: number[]) => {
    ApiService.contributors.getBulkMerge(contributorIds).then((response) => {
      setBulkMerge(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchResource = () => {
    ApiService.contributors.getBulkMergeResource().then((response) => {
      setResource(response);
    });
  };

  const save = (bulkMerge: ContributorBulkMerge) => {
    setValidationErrors({});

    ApiService.contributors
      .saveBulkMerge(bulkMerge)
      .then(() => {
        navigate(`../`);

        listContext.loadList?.();
        listContext.clearSelectedIds?.();
        dispatch(setAppToast(newToast("success")));
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing="sm">
            <Typography variant="inherit">{contributorBulkMergePageRoute.handle}</Typography>

            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Stack>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            {activeStep > 0 && <Button onClick={() => setActiveStep(activeStep - 1)}>Previous</Button>}

            {activeStep < 1 && (
              <Button
                onClick={() => setActiveStep(activeStep + 1)}
                variant="outlined"
                disabled={!bulkMerge.mergeToContributorId}
              >
                Next
              </Button>
            )}

            {activeStep === 1 && (
              <SubmitButton
                onClick={() => save(bulkMerge)}
                disabled={!(bulkMerge.contributors && bulkMerge.contributors?.length > 0 && unsavedChanges)}
              />
            )}
          </Stack>
        </Stack>
      }
    >
      {activeStep === 0 && (
        <Contributors
          contributors={bulkMerge.contributors || []}
          resource={resource}
          mergeToContributorId={bulkMerge.mergeToContributorId}
          setMergeContributorId={(id: number) =>
            setBulkMerge(
              produce(bulkMerge, (draft) => {
                draft.mergeToContributorId = id;
              })
            )
          }
        />
      )}

      {activeStep === 1 && (
        <ContributorEmployments
          employments={bulkMerge.employments || []}
          setEmployments={(employments) =>
            setBulkMerge(
              produce((draft) => {
                draft.employments = employments;
              })
            )
          }
          resource={resource}
          validationErrors={validationErrors[pn<ContributorBulkMerge>("employments")] || {}}
        />
      )}
    </Page>
  );
};
